import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import format from 'string-template';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import * as Sentry from '@sentry/browser';
import { useRouteMatch } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';

import { pathPlaceholder, storagePaths } from '../../../../../utils/firebasePaths';
import useStorage, { StorageProcessState } from '../../../../../hooks/useStorage';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useToast from '../../../../hooks/useToast';
import { WELCOME_MESSAGE_PLACEHOLDER } from '../../../../utils/appCustomization';
import AttachmentsContainer from '../../../../components/AttachmentsContainer';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import { MAX_FILE_UPLOAD_SIZE, MAX_FILE_UPLOAD_SIZE_MB } from '../../../../../utils/files';
import useAttachmentInputHandlers from '../../../../../hooks/useAttachmentInputHandlers';

import CustomizationContext, { withCustomizationContextReady } from '../../../../context/CustomizationContext';
import { Title, Description } from '../../styles';
import { PlaceholderButton, PlaceholdersContainer, PlaceholderTip } from '../../../../components/PlaceHolder/styles';
import commonTexts from '../texts.json';
import {
  ContentWrapper,
  InputContainer,
  StyledTextField,
  StyledButton,
  MessageContainer,
  StepToggle,
  StepToggleSwitch,
  Step2Title,
} from './styles';
import screenTexts from './texts.json';
import {
  getDefaultMessages,
  getDefaultPlaceholder,
  getStepFolder,
  buildMessagesWithAttachmentsFromResults,
  safeMessages,
  hasMessageContent,
  toMessageShape,
  buildUrlNameMap,
  normalizeAttachmentRef,
} from './utils';

const MAX_CHARACTERS_COUNT = 5000;
const MAX_ONBOARDING_MESSAGES = 2;

const texts = {
  ...commonTexts,
  ...screenTexts,
};

const OnboardingMessagesConfig = () => {
  const { appCustomizationDoc } = useContext(CustomizationContext);
  // Render-time read subscribes firestorter/MobX so effects see fresh data after remounts.
  const onboardingMessages = appCustomizationDoc?.onboardingMessages ?? null;
  const {
    params: { userId: coachId },
  } = useRouteMatch();

  const { uploadAttachments, deleteAttachments, getBlobUrl } = useStorage();
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  const { onFilesSelected, copyPlaceholderValue } = useAttachmentInputHandlers({
    showToast,
    fileSizeErrorText: texts.fileSizeError,
    maxFileSizeBytes: MAX_FILE_UPLOAD_SIZE,
    maxFileSizeMb: MAX_FILE_UPLOAD_SIZE_MB,
    placeholderCopiedText: texts.placeholderCopied,
  });

  const [isReady, setIsReady] = useState(false);
  const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // Orphan deletion is intentionally "delete on Save" (never on remove click).
  // We track what was initially visible + what the user removed during this session.
  const initialAttachmentsByUrlRef = React.useRef(new Map());
  const pendingDeleteAttachmentsByUrlRef = React.useRef(new Map());

  const [messages, setMessages] = useState(() => getDefaultMessages([]));

  const setInitialAttachmentsFromMessages = useCallback((msgs) => {
    initialAttachmentsByUrlRef.current = buildUrlNameMap(
      msgs.flatMap((m) => m.fileAttachments || []),
    );
  }, []);

  const buildOrphanedFromNewUrls = useCallback((newUrls) => {
    const urlsToDelete = new Set();

    // Attachments the user explicitly removed during this editing session.
    pendingDeleteAttachmentsByUrlRef.current.forEach((_name, url) => {
      if (url && !newUrls.has(url)) urlsToDelete.add(url);
    });

    // Safety net: attachments that existed when the page loaded but are no longer referenced.
    initialAttachmentsByUrlRef.current.forEach((_name, url) => {
      if (url && !newUrls.has(url)) urlsToDelete.add(url);
    });

    return [...urlsToDelete].map((url) => ({
      url,
      name: pendingDeleteAttachmentsByUrlRef.current.get(url)
        || initialAttachmentsByUrlRef.current.get(url)
        || url,
    }));
  }, []);

  const initStepAttachments = useCallback(
    async (attachmentRefs) => {
      if (!attachmentRefs.length) {
        return [];
      }
      // Load attachments eagerly so we can preview them in the UI.
      const results = await Promise.allSettled(
        attachmentRefs.map((att) => getBlobUrl(att.url).then((dataUrl) => ({ ...att, dataUrl, isUploaded: true }))),
      );
      return results.map((result, i) => {
        if (result.status === 'fulfilled') {
          return result.value;
        }
        Sentry.captureException(result.reason, {
          extra: { coachId, attachmentUrl: attachmentRefs[i]?.url },
        });
        return { ...attachmentRefs[i], isUploaded: true, loadFailed: true };
      });
    },
    [getBlobUrl, coachId],
  );

  const setFileAttachmentsForIndex = useCallback(
    (index) => (valueOrUpdater) => {
      setMessages((prev) => {
        if (index >= prev.length) {
          return prev;
        }
        const next = [...prev];
        const currentFiles = next[index]?.fileAttachments || [];
        const newFiles = typeof valueOrUpdater === 'function' ? valueOrUpdater(currentFiles) : valueOrUpdater;
        next[index] = { ...next[index], fileAttachments: newFiles };
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    if (isUploading || isReady) {
      return;
    }
    if (!appCustomizationDoc) {
      setMessages(getDefaultMessages([]));
      setIsReady(true);
      return;
    }

    const init = async () => {
      if (!isComponentMountedRef.current) {
        return;
      }

      const data = onboardingMessages || [];
      pendingDeleteAttachmentsByUrlRef.current.clear();
      try {
        setIsAttachmentsLoading(true);
        const results = await Promise.all(
          data.map((m) => initStepAttachments(m.attachments || [])),
        );

        const finalMessages = buildMessagesWithAttachmentsFromResults(data, results);
        // Tighten safety net: only consider attachments that actually loaded (and are therefore visible).
        // This prevents deleting attachments just because they failed to load and were filtered from UI state.
        setInitialAttachmentsFromMessages(finalMessages);

        if (isComponentMountedRef.current) {
          setMessages(safeMessages(finalMessages));
          const hasFailures = results.some((files) => files.some((f) => f.loadFailed));
          if (hasFailures) {
            showToast(texts.loadAttachmentsError, { error: true });
          }
        }
      } catch (err) {
        Sentry.captureException(err, { extra: { coachId } });
        if (isComponentMountedRef.current) {
          showToast(texts.loadAttachmentsError, { error: true });
          const messagesWithFailedAttachments = data.map((m) => ({ ...toMessageShape(m), fileAttachments: [] }));
          setMessages(safeMessages(messagesWithFailedAttachments));
        }
      } finally {
        if (isComponentMountedRef.current) {
          setIsAttachmentsLoading(false);
          setIsReady(true);
        }
      }
    };
    init();
  }, [
    appCustomizationDoc,
    onboardingMessages,
    isReady,
    isUploading,
    initStepAttachments,
    isComponentMountedRef,
    setInitialAttachmentsFromMessages,
    showToast,
    coachId,
  ]);

  const uploadMessageAttachments = useCallback(
    async (attachments, stepFolder) => {
      const { existing, toUpload } = attachments.reduce(
        (acc, f) => {
          if (f.isUploaded) {
            acc.existing.push({ name: f.name, url: f.url });
          } else {
            acc.toUpload.push(f);
          }
          return acc;
        },
        { existing: [], toUpload: [] },
      );

      const basePath = format(storagePaths.COACH_ONBOARDING_MESSAGE_ATTACHMENTS, {
        [pathPlaceholder.COACH_ID]: coachId,
      });
      const storagePath = `${basePath}/${stepFolder}`;

      try {
        // Upload new files; failures are tolerated (we still save what succeeded).
        const result = await uploadAttachments(toUpload, storagePath);
        const successfulUploads = result
          .map((res, i) => res.state === StorageProcessState.SUCCESS && { name: toUpload[i].name, url: res.fileRef })
          .filter(Boolean);
        const storageTaskFailed = successfulUploads.length !== toUpload.length;
        const failedFileNames = result
          .map((res, i) => res.state !== StorageProcessState.SUCCESS && toUpload[i]?.name)
          .filter(Boolean);

        return {
          attachmentsRefs: [...existing, ...successfulUploads],
          storageTaskFailed,
          failedFileNames,
        };
      } catch (error) {
        Sentry.captureException(error, { extra: { coachId, stepFolder } });
        return {
          attachmentsRefs: [...existing],
          storageTaskFailed: true,
          failedFileNames: toUpload.map((f) => f.name).filter(Boolean),
        };
      }
    },
    [coachId, uploadAttachments],
  );

  const updateMessage = useCallback((index, updates) => {
    setMessages((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  }, []);

  const handleToggleEnabled = useCallback(
    (index) => (event) => {
      const { checked } = event.target;
      if (checked && !hasMessageContent(messages[index])) {
        showToast(texts.messageEmpty, { error: true });
        return;
      }
      updateMessage(index, { enabled: checked });
    },
    [messages, updateMessage, showToast],
  );

  const handleTextChange = useCallback(
    (index) => (event) => {
      const newValue = event.target.value;
      updateMessage(index, { message: newValue });
    },
    [updateMessage],
  );

  const addMessage = useCallback(() => {
    setMessages((prev) => {
      if (prev.length >= MAX_ONBOARDING_MESSAGES) {
        return prev;
      }
      return [...prev, { ...toMessageShape({ message: '', enabled: false }) }];
    });
  }, []);

  const handleFileRemoved = useCallback((index) => (fileIndex) => {
    setMessages((prev) => {
      const files = prev[index]?.fileAttachments || [];
      const fileToRemove = files[fileIndex];
      const ref = normalizeAttachmentRef(fileToRemove);
      if (fileToRemove?.isUploaded && ref?.url) {
        // Mark for deletion; actual Storage deletion happens only when Save is clicked.
        pendingDeleteAttachmentsByUrlRef.current.set(ref.url, ref.name);
      }
      const newFiles = files.filter((_, i) => i !== fileIndex);
      const next = [...prev];
      next[index] = { ...next[index], fileAttachments: newFiles };
      return next;
    });
  }, []);

  const removeMessage = useCallback((index) => {
    if (index < 1) {
      return;
    }
    setMessages((prev) => {
      const messageToRemove = prev[index];
      const fileAttachments = messageToRemove?.fileAttachments || [];
      fileAttachments.forEach((file) => {
        const ref = normalizeAttachmentRef(file);
        if (file?.isUploaded && ref?.url) {
          // Mark message attachments for deletion (delete-on-save).
          pendingDeleteAttachmentsByUrlRef.current.set(ref.url, ref.name);
        }
      });
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const resetMessagesToDocState = useCallback(async () => {
    const data = appCustomizationDoc?.onboardingMessages || [];
    pendingDeleteAttachmentsByUrlRef.current.clear();
    const results = await Promise.all(
      data.map((m) => initStepAttachments(m.attachments || [])),
    );
    const finalMessages = buildMessagesWithAttachmentsFromResults(data, results);
    setInitialAttachmentsFromMessages(finalMessages);
    setMessages(safeMessages(finalMessages));
  }, [appCustomizationDoc, initStepAttachments, setInitialAttachmentsFromMessages]);

  const handleOrphanDeleteFailure = useCallback(
    async (failedFileNames) => {
      showToast(
        format(texts.deletePartialFailure, { fileNames: failedFileNames.join(', ') || 'attachments' }),
        { error: true },
      );
      await resetMessagesToDocState();
    },
    [resetMessagesToDocState, showToast],
  );

  const deleteOrphanedAttachments = useCallback(
    async (orphaned) => {
      const deleteResults = await deleteAttachments(orphaned.map((a) => ({ storagePath: a.url })));
      const failedDeletes = deleteResults.filter((r) => r.state !== StorageProcessState.SUCCESS);
      if (failedDeletes.length > 0) {
        const failedFileNames = failedDeletes.map(
          (f) => orphaned.find((o) => o.url === f.fileRef)?.name || f.fileRef,
        );
        await handleOrphanDeleteFailure(failedFileNames);
        return false;
      }
      return true;
    },
    [deleteAttachments, handleOrphanDeleteFailure],
  );

  const isError = useMemo(() => messages.some((m) => (m.message || '').length > MAX_CHARACTERS_COUNT), [messages]);

  const canAddMessage = useMemo(() => {
    if (messages.length >= MAX_ONBOARDING_MESSAGES) {
      return false;
    }
    const lastMessage = messages[messages.length - 1];
    return !lastMessage || hasMessageContent(lastMessage);
  }, [messages]);

  const handleSave = useCallback(async () => {
    if (!appCustomizationDoc) {
      return;
    }
    if (isUploading) {
      return;
    }

    setIsUploading(true);
    try {
      const results = await Promise.all(
        messages.map((m, i) => uploadMessageAttachments(m.fileAttachments || [], getStepFolder(i))),
      );

      const kept = messages
        .map((m, i) => ({ m, attachmentsRefs: results[i]?.attachmentsRefs || [] }))
        .filter(({ m, attachmentsRefs }) => (m.message || '').trim() || attachmentsRefs.length > 0);

      const messagesToSave = kept.map(({ m, attachmentsRefs }) => ({
        enabled: !!m.enabled,
        message: m.message || '',
        attachments: attachmentsRefs,
      }));

      const newUrls = new Set(
        messagesToSave.flatMap((m) => (m.attachments || []).map((a) => a.url)),
      );
      // Delete anything no longer referenced, based on (a) what user removed
      // this session and (b) what was visible on load.
      const orphaned = buildOrphanedFromNewUrls(newUrls);

      if (orphaned.length > 0) {
        try {
          if (!(await deleteOrphanedAttachments(orphaned))) {
            return;
          }
        } catch (err) {
          Sentry.captureException(err, { extra: { coachId } });
          await handleOrphanDeleteFailure(orphaned.map((a) => a.name).filter(Boolean));
          return;
        }
      }

      await appCustomizationDoc.setOnboardingMessages(messagesToSave);
      showToast(texts.messageSaved);
      const failedFileNames = results.flatMap((r) => r.failedFileNames || []);
      if (failedFileNames.length > 0) {
        showToast(format(texts.uploadPartialFailure, { fileNames: failedFileNames.join(', ') }), { error: true });
      }
    } catch (err) {
      Sentry.captureException(err, { extra: { coachId } });
      showToast(texts.messageSaveFailed, { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsUploading(false);
        setIsReady(false); // triggers useEffect re-init from fresh firestorter snapshot
      }
    }
  }, [
    appCustomizationDoc,
    buildOrphanedFromNewUrls,
    coachId,
    deleteOrphanedAttachments,
    handleOrphanDeleteFailure,
    isComponentMountedRef,
    isUploading,
    messages,
    uploadMessageAttachments,
    showToast,
  ]);

  return (
    <ContentWrapper>
      <Title>{texts.title}</Title>
      <Description>{texts.description}</Description>

      {messages.map((msg, index) => (
        <React.Fragment key={msg.id}>
          {index > 0 ? (
            <Step2Title>{format(texts.stepTitleTemplate, { n: index + 1 })}</Step2Title>
          ) : (
            <Title>{format(texts.stepTitleTemplate, { n: index + 1 })}</Title>
          )}
          <Description>{texts.stepDescription}</Description>
          {index > 0 && (
            <StepToggle
              control={<StepToggleSwitch checked={msg?.enabled} onChange={handleToggleEnabled(index)} />}
              label={texts.stepToggleLabel}
            />
          )}
          <MessageContainer>
            <InputContainer>
              <StyledTextField
                value={msg?.message ?? ''}
                onChange={handleTextChange(index)}
                placeholder={getDefaultPlaceholder(index)}
                error={(msg?.message?.length ?? 0) > MAX_CHARACTERS_COUNT}
                helperText={format(texts.characterCount, {
                  count: msg?.message?.length ?? 0,
                  maxCount: MAX_CHARACTERS_COUNT,
                })}
                multiline
                maxRows={18}
              />
              <AttachmentsContainer
                fileAttachments={msg?.fileAttachments ?? []}
                onFileRemoved={handleFileRemoved(index)}
                onFilesSelected={(event) => onFilesSelected(event, setFileAttachmentsForIndex(index))}
                isUploading={isUploading && isReady}
              />
            </InputContainer>
            {index === 0 && (
              <PlaceholdersContainer>
                <PlaceholderTip>{texts.placeholderTitle}</PlaceholderTip>
                {Object.values(WELCOME_MESSAGE_PLACEHOLDER).map((placeholderValue) => (
                  <PlaceholderButton
                    key={placeholderValue}
                    startIcon={<CopyAllIcon />}
                    onClick={() => copyPlaceholderValue(placeholderValue)}
                  >
                    {`{${placeholderValue}}`}
                  </PlaceholderButton>
                ))}
                <PlaceholderTip>{texts.placeholderDescription}</PlaceholderTip>
              </PlaceholdersContainer>
            )}
          </MessageContainer>
          {index > 0 && (
            <StyledButton
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => removeMessage(index)}
              sx={{ mb: 2 }}
            >
              {texts.removeMessageButton}
            </StyledButton>
          )}
        </React.Fragment>
      ))}

      {messages.length < MAX_ONBOARDING_MESSAGES && (
        <StyledButton
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addMessage}
          disabled={!canAddMessage}
          sx={{ mb: 2 }}
        >
          {texts.addMessageButton}
        </StyledButton>
      )}

      <StyledButton
        variant="contained"
        onClick={handleSave}
        disabled={!isReady || isAttachmentsLoading || isError || isUploading}
        startIcon={<SaveIcon />}
      >
        {texts.saveButton}
      </StyledButton>

      <LoadingOverlay isLoading={!isReady || isAttachmentsLoading} />
    </ContentWrapper>
  );
};

export default compose(withCustomizationContextReady, observer)(OnboardingMessagesConfig);
